import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, QrCode, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QrSingleUseWarning } from "@/components/qr-single-use-warning";
import { refreshBoideUser } from "@/hooks/use-boide-user";
import {
  createSepayOrder,
  ensureQuickTopupOffer,
  getMonthlyTopupProgress,
  getSepayOrderStatus,
  getTopupBonusCampaign,
  type MonthlyTopupProgress,
  type SepayOrder,
  type TopupBonusCampaign,
} from "@/lib/boide-api";
import {
  formatQuickTopupCountdown,
  isQuickTopupOfferApplicable,
  isQuickTopupOfferError,
  quickTopupClockSkewMs,
  stampQuickTopupOffer,
  type QuickTopupOffer,
} from "@/lib/quick-topup";

const AMOUNTS = [20_000, 50_000, 100_000, 500_000, 1_000_000];
const MIN_TOPUP_AMOUNT = 20_000;
const PAID_STATUSES = new Set(["paid", "success", "completed", "complete"]);
const POLL_INTERVAL_MS = 4_000;

function money(value: number) {
  return `${value.toLocaleString("vi-VN")}đ`;
}

function isPaid(order: SepayOrder | null) {
  return Boolean(order && PAID_STATUSES.has(order.status.trim().toLowerCase()));
}

function campaignBonus(campaign: TopupBonusCampaign | null, amount: number) {
  if (!campaign?.active) return 0;
  return campaign.tiers
    .filter((tier) => tier.is_active !== false && tier.threshold_amount <= amount)
    .reduce((best, tier) => Math.max(best, tier.bonus_amount), 0);
}

export function QuickTopupDialog({
  open,
  onOpenChange,
  onOfferChange,
  reason = "Số dư của bạn không đủ để tiếp tục.",
  initialOffer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOfferChange?: (offer?: QuickTopupOffer) => void;
  reason?: string;
  initialOffer?: QuickTopupOffer;
}) {
  const [amount, setAmount] = useState(50_000);
  const [order, setOrder] = useState<SepayOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [offer, setOffer] = useState<QuickTopupOffer | undefined>(initialOffer);
  const [bonusCampaign, setBonusCampaign] = useState<TopupBonusCampaign | null>(null);
  const [monthlyProgress, setMonthlyProgress] = useState<MonthlyTopupProgress | null>(null);
  const [otherPromosLoading, setOtherPromosLoading] = useState(false);
  const [otherPromosError, setOtherPromosError] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const paidToastShown = useRef(false);
  const paid = isPaid(order);
  const qrUrl = order?.qr_url || order?.url_qr || "";

  const createOrder = useCallback(async () => {
    setLoading(true);
    setError("");
    paidToastShown.current = false;
    const offerId = isQuickTopupOfferApplicable(offer, amount) ? offer.id : undefined;
    try {
      try {
        setOrder(await createSepayOrder(amount, undefined, undefined, offerId));
      } catch (err) {
        // Offer stale (đã dùng/hết hạn giữa chừng) — bỏ ưu đãi và tạo lại đơn
        // để user luôn nạp được.
        if (!offerId || !isQuickTopupOfferError(err)) throw err;
        const consumedOffer = offer ? { ...offer, consumed: true } : undefined;
        setOffer(consumedOffer);
        onOfferChange?.(consumedOffer);
        setOrder(await createSepayOrder(amount));
        toast.warning(
          "Quick Topup đã hết hạn hoặc đã dùng — hệ thống đã chuyển sang ưu đãi nạp thông thường.",
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không tạo được mã QR. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  }, [amount, offer, onOfferChange]);

  useEffect(() => {
    if (!open) return;
    setOffer(stampQuickTopupOffer(initialOffer));
    setNow(Date.now());
    if (!initialOffer) {
      void ensureQuickTopupOffer()
        .then((nextOffer) => {
          const stamped = stampQuickTopupOffer(nextOffer);
          setOffer(stamped);
          onOfferChange?.(stamped);
        })
        .catch(() => undefined);
    }
  }, [open, initialOffer, onOfferChange]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setOtherPromosLoading(true);
    setOtherPromosError("");

    void Promise.allSettled([getTopupBonusCampaign(), getMonthlyTopupProgress()]).then(
      ([campaignResult, progressResult]) => {
        if (cancelled) return;
        if (campaignResult.status === "fulfilled") {
          setBonusCampaign(campaignResult.value);
        } else {
          setBonusCampaign(null);
        }
        if (progressResult.status === "fulfilled") {
          setMonthlyProgress(progressResult.value);
        } else {
          setMonthlyProgress(null);
        }
        if (campaignResult.status === "rejected" && progressResult.status === "rejected") {
          setOtherPromosError("Chưa tải được các ưu đãi nạp khác. Vui lòng thử lại.");
        }
        setOtherPromosLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [open]);

  const offerId = offer?.id;
  const offerConsumed = offer?.consumed;
  const offerExpired = offer?.expired;
  const offerMinAmount = offer?.min_amount;
  const offerMaxAmount = offer?.max_amount;

  useEffect(() => {
    if (
      !open ||
      !offerId ||
      offerConsumed ||
      offerExpired ||
      offerMinAmount == null ||
      offerMaxAmount == null
    ) {
      return;
    }
    setAmount((current) =>
      current >= offerMinAmount && current <= offerMaxAmount ? current : Math.ceil(offerMinAmount),
    );
    setOrder(null);
    setError("");
  }, [offerConsumed, offerExpired, offerId, offerMaxAmount, offerMinAmount, open]);

  useEffect(() => {
    if (!open || !offer || offer.consumed) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [open, offer]);

  useEffect(() => {
    if (!open || !order?.code || paid) return;
    const timer = window.setInterval(async () => {
      try {
        const nextOrder = await getSepayOrderStatus(order.code);
        setOrder((current) => ({ ...(current ?? nextOrder), ...nextOrder }));
        if (isPaid(nextOrder)) {
          await refreshBoideUser();
          if (!paidToastShown.current) {
            paidToastShown.current = true;
            toast.success("Nạp tiền thành công. Bạn có thể tiếp tục phiên xem.");
          }
        }
      } catch {
        // Poll nền: giữ QR hiện tại và thử lại ở nhịp kế tiếp.
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [open, order?.code, paid]);

  const selectAmount = (nextAmount: number) => {
    if (nextAmount === amount) return;
    setAmount(nextAmount);
    setOrder(null);
    setError("");
  };

  const handleAmountInput = (value: string) => {
    const digits = value.replace(/\D/g, "");
    selectAmount(digits ? Number(digits) : 0);
  };

  // Countdown neo theo giờ SERVER: now (đồng hồ client) + skew = giờ server ước
  // tính — máy user chỉnh sai giờ bao nhiêu cũng không lệch hạn ưu đãi thật.
  const serverNow = now + quickTopupClockSkewMs(offer);
  const remainingSeconds = offer
    ? Math.max(0, Math.ceil((new Date(offer.expires_at).getTime() - serverNow) / 1_000))
    : 0;
  const offerActive = Boolean(offer && !offer.consumed && remainingSeconds > 0);
  const offerApplicable = isQuickTopupOfferApplicable(offer, amount);
  const bonusPercent = offer ? Math.round(offer.bonus_rate * 100) : 0;
  const quickBonus = offerApplicable ? Math.round(amount * (offer?.bonus_rate ?? 0)) : 0;
  const otherBonusForAmount = (value: number) =>
    Math.max(
      campaignBonus(bonusCampaign, value),
      Math.round(value * (monthlyProgress?.unlocked_rate ?? 0)),
    );
  const fixedBonus = campaignBonus(bonusCampaign, amount);
  const monthlyBonus = Math.round(amount * (monthlyProgress?.unlocked_rate ?? 0));
  const otherBonus = Math.max(fixedBonus, monthlyBonus);
  const usingQuickTopup = offerActive && offerApplicable;
  const showOtherPromos = !usingQuickTopup;
  const bestBonus = usingQuickTopup ? quickBonus : otherBonus;
  const bonusSource = usingQuickTopup
    ? "Quick Topup"
    : monthlyBonus >= fixedBonus && monthlyBonus > 0
      ? `Ưu đãi mốc nạp +${Math.round((monthlyProgress?.unlocked_rate ?? 0) * 100)}%`
      : fixedBonus > 0
        ? bonusCampaign?.name || "Thưởng gói nạp"
        : "Ưu đãi nạp tiền";
  const amountOptions =
    offerActive && offer
      ? Array.from(
          new Set([...AMOUNTS, Math.ceil(offer.min_amount), Math.floor(offer.max_amount)]),
        ).sort((a, b) => a - b)
      : AMOUNTS;
  const amountError =
    amount < MIN_TOPUP_AMOUNT ? `Số tiền nạp tối thiểu là ${money(MIN_TOPUP_AMOUNT)}.` : "";
  const { text: countdown, unit: countdownUnit } = formatQuickTopupCountdown(remainingSeconds);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Căn giữa trên mọi kích thước màn hình; nội dung dài cuộn bên trong
          popup để không tràn khỏi viewport mobile. */}
      <DialogContent className="!left-1/2 !right-auto !top-1/2 w-[calc(100vw-1.5rem)] max-w-md min-w-0 !-translate-x-1/2 !-translate-y-1/2 max-h-[calc(100dvh-1.5rem)] overflow-x-hidden overflow-y-auto overscroll-contain rounded-2xl border-gold-soft/60 bg-card/95 p-4 [scrollbar-gutter:stable] backdrop-blur-xl sm:p-6">
        <DialogHeader className="text-left">
          <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-gold-gradient text-primary-foreground shadow-gold">
            <Wallet className="h-5 w-5" />
          </div>
          <DialogTitle className="font-serif-soft text-2xl">Nạp nhanh để tiếp tục</DialogTitle>
          <DialogDescription>{reason}</DialogDescription>
        </DialogHeader>

        {offerActive && offer ? (
          <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm">
            Nạp trong <strong>{countdownUnit ? `${countdown} ${countdownUnit}` : countdown}</strong>{" "}
            để nhận thêm <strong>{bonusPercent}%</strong>. Ưu đãi chỉ áp dụng cho khoản nạp từ{" "}
            <strong> {money(offer.min_amount)}</strong> đến{" "}
            <strong>{money(offer.max_amount)}</strong>. Bạn vẫn có thể nạp ngoài khoảng này và hệ
            thống sẽ xét các ưu đãi nạp khác.
          </div>
        ) : null}
        {offerActive && !offerApplicable ? (
          <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            Mức nạp {money(amount)} nằm ngoài khoảng Quick Topup nên không được cộng {bonusPercent}
            %. Hệ thống sẽ tự áp dụng ưu đãi nạp khác tốt nhất nếu có.
          </div>
        ) : null}
        {showOtherPromos && otherPromosLoading ? (
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            Đang tải các ưu đãi nạp khác...
          </div>
        ) : null}
        {showOtherPromos && !otherPromosLoading && bestBonus > 0 ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
            Nạp {money(amount)} được cộng thêm <strong>{money(bestBonus)}</strong> từ{" "}
            <strong>{bonusSource}</strong>.
          </div>
        ) : null}
        {showOtherPromos && !otherPromosLoading && !otherPromosError && bestBonus === 0 ? (
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            Hiện chưa có ưu đãi nạp thông thường nào áp dụng cho mức {money(amount)}.
          </div>
        ) : null}
        {showOtherPromos && otherPromosError ? (
          <p className="text-xs font-medium text-destructive">{otherPromosError}</p>
        ) : null}
        {paid ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <div>
              <p className="font-semibold text-foreground">Đã nạp thành công</p>
              <p className="mt-1 text-sm text-muted-foreground">Số dư đã được cập nhật.</p>
              {order?.bonus_amount ? (
                <p className="mt-1 text-sm text-emerald-600">
                  Bonus {money(order.bonus_amount)} · Nhận{" "}
                  {money(order.credited_amount ?? order.amount)}
                </p>
              ) : null}
            </div>
            <Button onClick={() => onOpenChange(false)}>Tiếp tục xem</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${Math.min(amountOptions.length, 4)}, minmax(0, 1fr))`,
              }}
              aria-label="Chọn số tiền nạp"
            >
              {amountOptions.map((value) => {
                const optionBonus = isQuickTopupOfferApplicable(offer, value)
                  ? Math.round(value * (offer?.bonus_rate ?? 0))
                  : otherBonusForAmount(value);
                return (
                  <Button
                    key={value}
                    type="button"
                    variant={amount === value ? "default" : "outline"}
                    className="h-auto min-h-9 flex-col gap-0.5 px-2 py-2 text-xs sm:text-sm"
                    onClick={() => selectAmount(value)}
                  >
                    <span>{value / 1_000}k</span>
                    {optionBonus > 0 ? (
                      <span className="text-[10px] font-normal opacity-80">
                        +{optionBonus / 1_000}k
                      </span>
                    ) : null}
                  </Button>
                );
              })}
            </div>

            <label className="block">
              <span className="mb-2 block text-xs font-medium text-muted-foreground">
                Hoặc nhập số tiền muốn nạp
              </span>
              <div className="relative">
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label="Nhập số tiền nạp"
                  value={amount > 0 ? amount.toLocaleString("vi-VN") : ""}
                  onChange={(event) => handleAmountInput(event.target.value)}
                  className="h-11 pr-10 text-base font-semibold"
                  placeholder={
                    offerActive && offer
                      ? `Từ ${money(offer.min_amount)}`
                      : `Từ ${money(MIN_TOPUP_AMOUNT)}`
                  }
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                  đ
                </span>
              </div>
              {amountError ? (
                <p className="mt-2 text-xs font-medium text-destructive">{amountError}</p>
              ) : null}
            </label>

            {order ? (
              <>
                <QrSingleUseWarning />
                <div className="rounded-2xl border border-gold-soft/50 bg-white p-3 text-center text-slate-900">
                  {qrUrl ? (
                    <img
                      src={qrUrl}
                      alt={`QR nạp ${money(order.amount)}`}
                      className="mx-auto aspect-square w-full max-w-[260px] object-contain"
                    />
                  ) : (
                    <div className="flex aspect-square max-h-[260px] items-center justify-center text-slate-500">
                      Không có QR
                    </div>
                  )}
                  <p className="mt-2 text-sm font-semibold">Quét QR để nạp {money(order.amount)}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Hệ thống tự cập nhật sau khi nhận tiền.
                  </p>
                </div>
              </>
            ) : (
              <Button
                className="h-auto w-full min-w-0 whitespace-normal px-3 py-3 text-left sm:px-4"
                onClick={() => void createOrder()}
                disabled={loading || Boolean(amountError)}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <QrCode className="h-4 w-4" />
                )}
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="whitespace-normal">Tạo QR nạp {money(amount)}</span>
                  {bestBonus > 0 ? (
                    <span className="whitespace-normal break-words text-xs font-normal opacity-90">
                      {bonusSource} cộng thêm {money(bestBonus)} · Tổng nhận{" "}
                      {money(amount + bestBonus)}
                    </span>
                  ) : null}
                </span>
              </Button>
            )}

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
